import React, { useEffect, useState } from 'react'
import get from 'lodash/get'
import RelayError from '../../../errors/errors'
import Textbox from '../Textbox/Textbox'
import Container from '../../Container/Container'
import Column from '../../Column/Column'

interface InputListProps {
    data: any[]
    value: any
    template?: any
    isDirty?: boolean
    showFilter?: boolean
    allOption?: boolean
    textField?: string
    valueField?: string
    onChange?: (value) => any
    handleMassUpdate?: (value) => any
    closeOnItemClick?: boolean
    dataItemTemplate?: (dataItem) => string | HTMLElement | React.ReactElement
    setPopupShow?: (show) => any
    noDataMessage?: string | HTMLElement | React.ReactElement
    placeholder?: string
    noNullOption?: boolean
    isLoading?: boolean
    isFetching?: boolean
    isDisabledKey?: string
    isDisabledMessageKey?: string
}

const InputList = ({
    data,
    value,
    template,
    showFilter,
    placeholder,
    allOption,
    textField,
    valueField,
    onChange,
    dataItemTemplate,
    closeOnItemClick = false,
    handleMassUpdate,
    setPopupShow,
    noNullOption,
    noDataMessage = 'No items found',
    isLoading,
    isFetching,
    isDirty = true,
    isDisabledKey,
    isDisabledMessageKey,
}: InputListProps) => {
    const [filterValue, setFilterValue] = useState('')
    const [filteredData, setFilteredData] = useState(data || [])

    useEffect(() => {
        setFilteredData(data)
    }, [data])

    useEffect(() => {
        // console.log('MOUNTING')
    }, [])

    const onClick = (dataItem) => (e) => {
        e.stopPropagation()
        onChange(dataItem)
        if (closeOnItemClick) {
            setPopupShow(false)
        }
    }

    const onClickPlaceholder = (e) => {
        e.stopPropagation()
        onChange(null)
        setPopupShow(false)
    }

    const onClickAllOption = (e) => {
        e.stopPropagation()

        if (value.length === filteredData.length) {
            handleMassUpdate([])
        } else {
            handleMassUpdate(
                filteredData.filter((d) => {
                    if (isDisabledKey) {
                        return !d[isDisabledKey]
                    }
                    return true
                }),
            )
        }
        setPopupShow(false)
    }

    const isDataItemSelected = (dataItem) => {
        const _value = Array.isArray(value) ? value : [value]
        const stringifiedValues = _value.map((v) => JSON.stringify(v))
        let dataItemValue = dataItem

        if (typeof dataItem === 'object') {
            dataItemValue = get(dataItem, valueField)
        }

        const stringifiedDataItemValue = JSON.stringify(dataItemValue)
        return Boolean(stringifiedValues.find((d) => d === stringifiedDataItemValue))
    }

    useEffect(() => {
        if (!data || !Array.isArray(data)) {
            return
        }
        const filtered = data.filter((d) => {
            let text = d
            if (textField) {
                text = get(d, textField)
            }
            if (text === undefined) {
                return null
            }
            text = text.toString()
            return text.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1
        })
        setFilteredData(filtered)
    }, [filterValue])

    const renderItems = () => {
        if (!isDirty) {
            return <div className={'r-input-data-loading-message'}>Please search</div>
        }

        if ((isLoading || isFetching) && filteredData.length === 0) {
            return <div className={'r-input-data-loading-message'}>Searching</div>
        }

        if (!filteredData || filteredData.length === 0) {
            return <div className={'r-input-no-data-message'}>{noDataMessage}</div>
        }

        const mapped = filteredData.map((d, i) => {
            let text = d
            if (typeof d === 'object') {
                if (!textField) {
                    throw new RelayError(
                        'Input list: dataitem is an object with no textField defined. Please add a textField prop',
                    )
                }
                text = get(d, textField)
            }

            const isSelected = isDataItemSelected(d)
            const isDisabled = isDisabledKey ? d[isDisabledKey] : false
            const isDisabledMessage = isDisabledMessageKey ? d[isDisabledMessageKey] : null
            const _onClick = isDisabled ? null : onClick(d)

            return (
                <div
                    key={i}
                    onClick={_onClick}
                    className={`r-input-list-item ${isDisabled ? 'r-state-disabled' : ''} ${
                        isSelected ? 'r-state-selected' : ''
                    }`}
                >
                    {dataItemTemplate
                        ? dataItemTemplate({ dataItem: d, isSelected, data, value, textField, valueField })
                        : text}
                    {isDisabledMessage ? (
                        <div className={'r-list-item-disabled-message'}>{isDisabledMessage}</div>
                    ) : null}
                </div>
            )
        })

        if (placeholder && !noNullOption) {
            mapped.unshift(
                <div className='r-input-list-item r-input-list-placeholder' onClick={onClickPlaceholder} key={-1}>
                    {placeholder}
                </div>,
            )
        }

        if (allOption && Array.isArray(filteredData)) {
            const val = value || []
            const text = val.length === filteredData.length ? 'Unselect All' : 'Select All'
            mapped.unshift(
                <div className='r-input-list-item r-input-list-placeholder' onClick={onClickAllOption} key={-1}>
                    {text}
                </div>,
            )
        }

        return mapped
    }

    return (
        <div className={'r-input-list-container'}>
            {showFilter ? (
                <div className={'r-input-list-filter-container'}>
                    <Container spacing={4}>
                        <Column>
                            <Textbox
                                noLabel
                                hideValidationContainer
                                placeholder={'Filter'}
                                name={'inputFilter'}
                                value={filterValue}
                                onChange={(val) => setFilterValue(val)}
                            />
                        </Column>
                    </Container>
                </div>
            ) : (
                ''
            )}
            {renderItems()}
        </div>
    )
}

export default InputList
